import React, { useEffect, useRef, useState } from "react";
import { ABtn, AResult, AScore } from "./arcadeUi";
import { sfx } from "@/lib/soundfx";

const makeTargets = () => Array.from({ length: 5 }, (_, i) => ({
  id: i, x: 18 + Math.random() * 64, y: 18 + Math.random() * 40, alive: true,
}));

export default function Marbles() {
  const boardRef = useRef(null);
  const [targets, setTargets] = useState(makeTargets);
  const [marble, setMarble] = useState({ x: 50, y: 90 });
  const [aim, setAim] = useState(null); // {x,y} pointer
  const [shots, setShots] = useState(7);
  const [score, setScore] = useState(0);
  const [rolling, setRolling] = useState(false);
  const raf = useRef(null);
  const drag = useRef(false);

  const alive = targets.filter((t) => t.alive).length;
  const over = shots <= 0 || alive === 0;

  const toPct = (e) => {
    const r = boardRef.current.getBoundingClientRect();
    return { x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 };
  };
  const down = (e) => { if (rolling || over) return; drag.current = true; setAim(toPct(e)); };
  const move = (e) => { if (!drag.current) return; setAim(toPct(e)); };
  const up = () => {
    if (!drag.current) return; drag.current = false;
    if (!aim) return;
    const dx = aim.x - marble.x, dy = aim.y - marble.y;
    const len = Math.hypot(dx, dy) || 1;
    shoot(dx / len, dy / len);
    setAim(null);
  };

  const shoot = (ux, uy) => {
    setRolling(true);
    setShots((s) => s - 1);
    let x = 50, y = 90;
    const speed = 2.4;
    const tick = () => {
      x += ux * speed; y += uy * speed;
      setMarble({ x, y });
      setTargets((ts) => ts.map((t) => {
        if (t.alive && Math.hypot(t.x - x, t.y - y) < 7) { setScore((v) => v + 1); sfx.click(); return { ...t, alive: false }; }
        return t;
      }));
      if (x < -5 || x > 105 || y < -5 || y > 105) {
        cancelAnimationFrame(raf.current);
        setRolling(false);
        setMarble({ x: 50, y: 90 });
        return;
      }
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
  };

  const reset = () => { setTargets(makeTargets()); setMarble({ x: 50, y: 90 }); setShots(7); setScore(0); setAim(null); setRolling(false); };
  useEffect(() => () => cancelAnimationFrame(raf.current), []);

  return (
    <div className="w-full text-center">
      <div className="flex justify-center gap-2">
        <AScore label="Hits" value={score} />
        <AScore label="Marbles left" value={alive} />
        <AScore label="Shots" value={shots} />
      </div>
      <p className="my-2 text-sm text-kirana-brown/70">Drag from your kancha to aim, release to shoot. Hit the marbles!</p>
      <div ref={boardRef} data-testid="marbles-board" onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up}
        style={{ touchAction: "none" }}
        className="relative mx-auto w-full max-w-[360px] cursor-crosshair overflow-hidden rounded-lg border-[3px] border-kirana-brown bg-[radial-gradient(circle,#caa472,#a9784f)]"
      >
        <div style={{ aspectRatio: "1/1" }} />
        {targets.map((t) => t.alive && (
          <span key={t.id} data-testid={`marble-target-${t.id}`} className="absolute h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border border-black/30"
            style={{ left: `${t.x}%`, top: `${t.y}%`, background: ["#E63946", "#457B9D", "#2A9D8F", "#E9C46A", "#6A0572"][t.id % 5], boxShadow: "inset -2px -2px 3px rgba(0,0,0,0.4), inset 2px 2px 3px rgba(255,255,255,0.5)" }} />
        ))}
        {aim && !rolling && (
          <svg className="pointer-events-none absolute inset-0 h-full w-full"><line x1={`${marble.x}%`} y1={`${marble.y}%`} x2={`${aim.x}%`} y2={`${aim.y}%`} stroke="#fff" strokeWidth="2" strokeDasharray="4 3" /></svg>
        )}
        <span className="absolute h-6 w-6 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-[#f1f1f1]"
          style={{ left: `${marble.x}%`, top: `${marble.y}%`, boxShadow: "inset -2px -2px 3px rgba(0,0,0,0.3)" }} data-testid="marbles-shooter" />
      </div>
      {over && (
        <>
          <AResult testId="marbles-result" win={score >= 3} text={`You hit ${score} / 5 marbles!`} />
          <div className="mt-3 flex justify-center"><ABtn data-testid="marbles-again" onClick={reset} tone="green">Play Again</ABtn></div>
        </>
      )}
    </div>
  );
}
